// ---------------------------------------------------------------------------
// ComparisonDimensionPickerModal -- Step 1: Choose Comparison Dimension
// ---------------------------------------------------------------------------
// Modal for selecting which dimension type to compare (Platform, Brand, etc.)
// Adapted from Funnel's DimensionPickerModal but simplified for Compare.
// No counts toggle - just dimension selection.
// ---------------------------------------------------------------------------

import React from 'react';
import { Building2, FileText, MapPin, Users } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { cn } from '@/lib/utils';
import { DIMENSION_GROUPS } from '@/config/comparisonDimensions';
import type { DimensionTypeConfig } from '@/types/comparison';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface ComparisonDimensionPickerModalProps {
  readonly open: boolean;
  readonly onClose: () => void;
  readonly onSelectDimension: (dimension: DimensionTypeConfig) => void;
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function ComparisonDimensionPickerModal({
  open,
  onClose,
  onSelectDimension,
}: ComparisonDimensionPickerModalProps) {
  const handleSelect = (dimension: DimensionTypeConfig) => {
    onSelectDimension(dimension);
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-5xl">
        <DialogHeader>
          <DialogTitle className="text-lg font-semibold text-v11-text-primary">
            Select Comparison Dimension
          </DialogTitle>
          <DialogDescription className="text-sm text-v11-text-muted">
            Choose the type of dimension you want to compare
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-6 max-h-[70vh] overflow-y-auto">
          {/* Saved Segments Section */}
          {DIMENSION_GROUPS.saved.length > 0 && (
            <div>
              <div className="flex items-center gap-2 text-xs uppercase tracking-wider text-v11-text-muted mb-4 font-semibold">
                <Users className="h-4 w-4" />
                Saved Segments
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-2">
                {DIMENSION_GROUPS.saved.map((dimension) => (
                  <button
                    key={dimension.type}
                    onClick={() => handleSelect(dimension)}
                    className={cn(
                      'text-left p-3 rounded-lg border transition-all',
                      'bg-v11-surface border-v11-border',
                      'hover:border-purple-500/40 hover:bg-purple-500/5 cursor-pointer'
                    )}
                  >
                    <div className="flex items-center justify-between mb-1">
                      <span className="font-medium text-v11-text-primary">
                        {dimension.label}
                      </span>
                    </div>
                    <div className="text-xs text-v11-text-muted">
                      {dimension.description}
                    </div>
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Entities Section */}
          <div>
            <div className="flex items-center gap-2 text-xs uppercase tracking-wider text-v11-text-muted mb-4 font-semibold">
              <Building2 className="h-4 w-4" />
              Entities
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-2">
              {DIMENSION_GROUPS.entities.map((dimension) => (
                <button
                  key={dimension.type}
                  onClick={() => handleSelect(dimension)}
                  className={cn(
                    'text-left p-3 rounded-lg border transition-all',
                    'bg-v11-surface border-v11-border',
                    'hover:border-blue-500/40 hover:bg-blue-500/5 cursor-pointer'
                  )}
                >
                  <div className="flex items-center justify-between mb-1">
                    <span className="font-medium text-v11-text-primary">
                      {dimension.label}
                    </span>
                  </div>
                  <div className="text-xs text-v11-text-muted">
                    {dimension.description}
                  </div>
                </button>
              ))}
            </div>
          </div>

          {/* Content Section */}
          <div>
            <div className="flex items-center gap-2 text-xs uppercase tracking-wider text-v11-text-muted mb-4 font-semibold">
              <FileText className="h-4 w-4" />
              Content
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-2">
              {DIMENSION_GROUPS.content.map((dimension) => (
                <button
                  key={dimension.type}
                  onClick={() => handleSelect(dimension)}
                  className={cn(
                    'text-left p-3 rounded-lg border transition-all',
                    'bg-v11-surface border-v11-border',
                    'hover:border-cyan-500/40 hover:bg-cyan-500/5 cursor-pointer'
                  )}
                >
                  <div className="flex items-center justify-between mb-1">
                    <span className="font-medium text-v11-text-primary">
                      {dimension.label}
                    </span>
                  </div>
                  <div className="text-xs text-v11-text-muted">
                    {dimension.description}
                  </div>
                </button>
              ))}
            </div>
          </div>

          {/* Demographics Section */}
          <div>
            <div className="flex items-center gap-2 text-xs uppercase tracking-wider text-v11-text-muted mb-4 font-semibold">
              <MapPin className="h-4 w-4" />
              Demographics
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-2">
              {DIMENSION_GROUPS.demographics.map((dimension) => (
                <button
                  key={dimension.type}
                  onClick={() => handleSelect(dimension)}
                  className={cn(
                    'text-left p-3 rounded-lg border transition-all',
                    'bg-v11-surface border-v11-border',
                    'hover:border-green-500/40 hover:bg-green-500/5 cursor-pointer'
                  )}
                >
                  <div className="flex items-center justify-between mb-1">
                    <span className="font-medium text-v11-text-primary">
                      {dimension.label}
                    </span>
                  </div>
                  <div className="text-xs text-v11-text-muted">
                    {dimension.description}
                  </div>
                </button>
              ))}
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
